import api from "./client";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeMessageResponse(data) {
  if (data?.message && typeof data.message === "object") {
    return data.message;
  }

  if (data?.data?.message && typeof data.data.message === "object") {
    return data.data.message;
  }

  return data;
}

function normalizeDisputeResponse(data) {
  if (data?.dispute && typeof data.dispute === "object") {
    return data.dispute;
  }

  if (data?.data?.dispute && typeof data.data.dispute === "object") {
    return data.data.dispute;
  }

  return data;
}

function unwrapData(response) {
  if (response && typeof response === "object" && "data" in response) {
    return response.data;
  }

  return response;
}

export function findRecoveredOrderMessage(previousMessages, nextMessages, submittedText, hadAttachment = false) {
  const previousIds = new Set(asArray(previousMessages).map((message) => message?.id).filter(Boolean));
  const createdMessages = asArray(nextMessages).filter((message) => !previousIds.has(message?.id));
  const text = String(submittedText || "").trim();

  if (createdMessages.length === 0) {
    return null;
  }

  const matchingMessage = createdMessages.find((message) => {
    const messageText = String(message?.message || "").trim();
    const hasAttachment = Boolean(message?.attachment_path || message?.attachment_name);

    if (hadAttachment) {
      return hasAttachment && (!text || messageText === text);
    }

    if (text && messageText === text) {
      return true;
    }

    return false;
  });

  if (matchingMessage) {
    return matchingMessage;
  }

  return hadAttachment ? null : createdMessages[createdMessages.length - 1];
}

function isNumericReference(value) {
  return /^\d+$/.test(String(value ?? "").trim());
}

function getAuthToken() {
  try {
    return (
      localStorage.getItem("mg_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("auth_token") ||
      localStorage.getItem("jwt") ||
      sessionStorage.getItem("mg_token") ||
      sessionStorage.getItem("token") ||
      sessionStorage.getItem("auth_token") ||
      sessionStorage.getItem("jwt") ||
      ""
    );
  } catch {
    return "";
  }
}

export function formatServiceCurrency(amount, currency = "USD") {
  const value = Number(amount ?? 0);

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: String(currency || "USD").toUpperCase(),
      maximumFractionDigits: value % 1 === 0 ? 0 : 2,
    }).format(value);
  } catch {
    return `${value.toFixed(value % 1 === 0 ? 0 : 2)} ${String(currency || "USD").toUpperCase()}`;
  }
}

export function getFreelancerServiceTitle(service, isAr = false) {
  if (!service) return "";

  const lang = isAr ? "ar" : "en";

  // New API may return `title` as an object: { ar: '', en: '' }
  if (service.title && typeof service.title === "object") {
    return service.title[lang] || service.title.en || service.title.ar || `Service #${service.id ?? ""}`;
  }

  return isAr
    ? service.title_ar || service.title_en || service.title || `Service #${service.id ?? ""}`
    : service.title_en || service.title_ar || service.title || `Service #${service.id ?? ""}`;
}

export function getFreelancerServiceSummary(service, isAr = false) {
  if (!service) return "";

  const lang = isAr ? "ar" : "en";

  // New API may return `short_description` as an object: { ar: '', en: '' }
  if (service.short_description && typeof service.short_description === "object") {
    return service.short_description[lang] || service.description?.[lang] || "";
  }

  return isAr
    ? service.short_description_ar || service.description_ar || service.short_description_en || service.description_en || service.short_description || service.description || ""
    : service.short_description_en || service.description_en || service.short_description_ar || service.description_ar || service.short_description || service.description || "";
}

export function getFreelancerServiceDescription(service, isAr = false) {
  if (!service) return "";

  const lang = isAr ? "ar" : "en";

  // New API may return `description` as an object: { ar: '', en: '' }
  if (service.description && typeof service.description === "object") {
    return service.description[lang] || service.description.en || service.description.ar || "";
  }

  return isAr
    ? service.description_ar || service.description_en || service.description || ""
    : service.description_en || service.description_ar || service.description || "";
}

// Public profile references are intentionally derived from the service payload.
// Different API versions expose the provider as `freelancer`, `provider`, or `user`.
export function getFreelancerProfileReference(service) {
  const freelancer = service?.freelancer || service?.provider || service?.user || {};
  const reference =
    freelancer?.username ||
    freelancer?.slug ||
    freelancer?.id ||
    service?.freelancer_id ||
    service?.provider_id ||
    service?.user_id ||
    "";

  return String(reference).trim();
}

export function getServiceFreelancer(service) {
  return service?.freelancer || service?.provider || service?.user || null;
}

export function isFreelancerServiceAvailable(service) {
  if (!service || !service.id) return false;

  if (
    service.is_active === false ||
    service.is_available === false ||
    service.available === false ||
    service.deleted_at
  ) {
    return false;
  }

  const status = String(service.status || "").trim().toLowerCase();
  const unavailableStatuses = [
    "inactive",
    "unavailable",
    "unpublished",
    "draft",
    "pending",
    "rejected",
    "archived",
    "suspended",
    "disabled",
    "deleted",
  ];

  const approvalStatus = String(service.approval_status || "").trim().toLowerCase();
  const unavailableApprovalStatuses = ["pending", "rejected", "draft", "suspended"];

  return (
    (!status || !unavailableStatuses.includes(status)) &&
    (!approvalStatus || !unavailableApprovalStatuses.includes(approvalStatus))
  );
}

export function getServiceDeliveryLabel(service, isAr = false) {
  const days = Number(service?.delivery_days ?? 0);
  if (!days) {
    return isAr ? "مرن" : "Flexible";
  }

  return isAr ? `${days} أيام` : `${days} days`;
}

export function getServiceRevisionsLabel(service, isAr = false) {
  const revisions = Number(service?.revisions ?? 0);
  return isAr ? `${revisions} مراجعات` : `${revisions} revisions`;
}

export async function listFreelancerServices(options = {}) {
  const response = await api.apiGet("freelancer-services", {
    cache: options.cache ?? true,
    ttl: options.ttl ?? 30_000,
  });

  return asArray(unwrapData(response));
}

export async function resolveFreelancerServiceId(serviceReference, knownServices = null) {
  const raw = String(serviceReference ?? "").trim();

  if (isNumericReference(raw)) {
    return Number(raw);
  }

  const services =
    knownServices || (await listFreelancerServices({ cache: true, ttl: 15_000 }));
  const normalized = raw.toLowerCase();
  const matched = services.find(
    (service) =>
      String(service?.slug || "").toLowerCase() === normalized ||
      String(service?.id || "") === raw
  );

  if (!matched?.id) {
    throw { status: 404, message: "Freelancer service not found" };
  }

  return Number(matched.id);
}

export async function getFreelancerServiceDetails(serviceReference) {
  const resolvedId = isNumericReference(serviceReference)
    ? Number(serviceReference)
    : await resolveFreelancerServiceId(serviceReference);

  const response = await api.apiGet(`freelancer-services/${resolvedId}`);
  return unwrapData(response);
}

export async function createServiceCheckout(serviceId, payload = {}) {
  return api.apiPost(`services/${serviceId}/checkout`, payload);
}

export async function confirmServicePayment({ sessionId = "", token = "", provider = "", mode = "" } = {}) {
  const params = new URLSearchParams();

  if (sessionId) params.set("session_id", sessionId);
  if (token) params.set("token", token);
  if (provider) params.set("provider", provider);
  if (mode) params.set("mode", mode);

  const response = await api.apiGet(
    `service-orders/payment/success?${params.toString()}`
  );

  return unwrapData(response);
}

export async function getMyServiceOrders() {
  const response = await api.apiGet("my-service-orders");
  return asArray(unwrapData(response));
}

export async function getMyFreelancerOrders() {
  const response = await api.apiGet("my-freelancer-orders");
  return asArray(unwrapData(response));
}

export async function getServiceOrderDetails(orderId) {
  const response = await api.apiGet(`service-orders/${orderId}`);
  return unwrapData(response);
}

export async function updateServiceOrderStatus(orderId, payload = {}) {
  const endpoint = api.buildUrl(`service-orders/${orderId}/status`);
  const token = getAuthToken();
  const response = await fetch(endpoint, {
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });

  const text = await response.text();
  let data = {};

  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = {};
  }

  if (!response.ok) {
    throw {
      status: response.status,
      message: data?.message || "Failed to update service order status.",
      body: data,
    };
  }

  return data;
}

export async function listServiceReviews(serviceReference) {
  const resolvedId = isNumericReference(serviceReference)
    ? Number(serviceReference)
    : await resolveFreelancerServiceId(serviceReference);

  const response = await api.apiGet(`freelancer-services/${resolvedId}/reviews`, {
    cache: true,
    ttl: 15_000,
  });

  return asArray(unwrapData(response));
}

export async function listServiceOrderMilestones(orderId) {
  const response = await api.apiGet(`service-orders/${orderId}/milestones`);
  return asArray(unwrapData(response));
}

export async function createServiceOrderMilestone(orderId, payload = {}) {
  const response = await api.apiPost(`service-orders/${orderId}/milestones`, payload);
  return unwrapData(response);
}

export async function updateServiceOrderMilestone(id, payload = {}) {
  return api.apiPut(`service-order-milestones/${id}`, payload);
}

export async function approveServiceOrderMilestone(id) {
  const response = await api.apiPost(`service-order-milestones/${id}/approve`, {});
  return unwrapData(response);
}

export async function listServiceOrderDisputes(orderId) {
  const response = await api.apiGet(`service-orders/${orderId}/disputes`);
  const data = unwrapData(response);
  return asArray(data?.disputes || data);
}

export async function openServiceOrderDispute(orderId, payload = {}) {
  const response = await api.apiPost(`service-orders/${orderId}/disputes`, payload);
  return normalizeDisputeResponse(unwrapData(response));
}

export async function listServiceOrderReviews(orderId) {
  const response = await api.apiGet(`service-orders/${orderId}/reviews`);
  return asArray(unwrapData(response));
}

export async function submitServiceOrderReview(orderId, payload = {}) {
  const response = await api.apiPost(`service-orders/${orderId}/reviews`, payload);
  return unwrapData(response);
}

export async function getOrderMessages(orderId) {
  // Fetch all messages without pagination (max 1000)
  const response = await api.apiGet(`service-orders/${orderId}/messages?per_page=1000`);
  const data = unwrapData(response);
  return asArray(data?.messages || data);
}

export async function sendOrderMessage(orderId, formData) {
  const response = await api.apiPost(`service-orders/${orderId}/messages`, formData);
  return normalizeMessageResponse(unwrapData(response));
}

export async function markOrderMessagesRead(orderId) {
  const response = await api.apiPost(`service-orders/${orderId}/messages/read`, {});
  return unwrapData(response);
}

export async function deliverServiceOrder(orderId, formData) {
  const response = await api.apiPost(`service-orders/${orderId}/deliver`, formData);
  return unwrapData(response);
}

export async function requestOrderCancellation(orderId, payload = {}) {
  const response = await api.apiPost(`service-orders/${orderId}/cancel-request`, payload);
  return unwrapData(response);
}

export async function respondOrderCancellation(orderId, payload = {}) {
  const response = await api.apiPost(`service-orders/${orderId}/cancel-respond`, payload);
  return unwrapData(response);
}

export async function acceptRejectDelivery(orderId, action, reason = null) {
  const payload = { action };
  if (reason) {
    payload.reason = reason;
  }
  const response = await api.apiPost(`v1/service-orders/${orderId}/accept-reject`, payload);
  return unwrapData(response);
}
